<template>
  <div class="price-history-container">
    <!-- 控制面板 -->
    <div class="d-flex justify-content-between align-items-center mb-3">
      <h6 class="mb-0">
        <i class="fas fa-chart-line me-2"></i>
        價格歷史記錄
      </h6>
      
      <!-- 時間範圍選擇器 -->
      <div class="btn-group btn-group-sm" role="group">
        <button
          v-for="period in availablePeriods"
          :key="period.value"
          type="button"
          class="btn"
          :class="selectedPeriod === period.value ? 'btn-primary' : 'btn-outline-primary'"
          @click="handlePeriodChange(period.value)"
          :disabled="loading"
        >
          {{ period.label }}
        </button>
      </div>
    </div>
    
    <!-- 載入狀態 -->
    <div v-if="loading" class="text-center py-4">
      <div class="spinner-border spinner-border-sm me-2" role="status"></div>
      <span class="text-muted">載入價格資料中...</span>
    </div>
    
    <!-- 錯誤狀態 -->
    <div v-else-if="error" class="alert alert-warning">
      <i class="fas fa-exclamation-triangle me-2"></i>
      {{ error }}
    </div>
    
    <!-- 價格統計資訊 -->
    <div v-else-if="priceStats" class="row mb-3">
      <div class="col-3">
        <div class="text-center p-2 bg-light rounded">
          <div class="small text-muted">最低價</div>
          <div class="fw-bold text-success">{{ formatPrice(priceStats.min) }}</div>
        </div>
      </div>
      <div class="col-3">
        <div class="text-center p-2 bg-light rounded">
          <div class="small text-muted">最高價</div>
          <div class="fw-bold text-danger">{{ formatPrice(priceStats.max) }}</div>
        </div>
      </div>
      <div class="col-3">
        <div class="text-center p-2 bg-light rounded">
          <div class="small text-muted">平均價</div>
          <div class="fw-bold text-primary">{{ formatPrice(priceStats.average) }}</div>
        </div>
      </div>
      <div class="col-3">
        <div class="text-center p-2 bg-light rounded">
          <div class="small text-muted">價差</div>
          <div class="fw-bold text-info">{{ formatPrice(priceStats.range) }}</div>
        </div>
      </div>
    </div>
    
    <!-- 圖表容器 -->
    <div class="chart-container" style="position: relative; height: 300px;">
      <canvas v-if="!loading && !error" ref="chartCanvas"></canvas>
      
      <!-- 無資料狀態 -->
      <div v-if="!loading && !error && (!priceHistory.price_history || Object.keys(priceHistory.price_history).length === 0)" 
           class="d-flex flex-column align-items-center justify-content-center h-100 text-muted">
        <i class="fas fa-chart-line fa-3x mb-3 opacity-50"></i>
        <p class="mb-0">此期間內無價格資料</p>
        <small>請嘗試選擇更長的時間範圍</small>
      </div>
    </div>
    
    <!-- 圖例和詳細資訊 -->
    <div v-if="priceHistory.price_history && Object.keys(priceHistory.price_history).length > 0" class="mt-3">
      <div class="row">
        <!-- <div class="col-md-6">
          <h6 class="small text-muted mb-2">通路資訊</h6>
          <div class="d-flex flex-wrap gap-2">
            <span 
              v-for="(data, channel) in priceHistory.price_history" 
              :key="channel"
              class="badge bg-secondary"
            >
              <i :class="getChannelIconClass(channel)" class="me-1"></i>
              {{ channel }} ({{ data.length }} 筆記錄)
            </span>
          </div>
        </div>
        <div class="col-md-6">
          <h6 class="small text-muted mb-2">資料範圍</h6>
          <div class="small">
            <div>開始：{{ formatDate(priceHistory.date_range?.start_date) }}</div>
            <div>結束：{{ formatDate(priceHistory.date_range?.end_date) }}</div>
            <div>總記錄：{{ priceHistory.total_records }} 筆</div>
          </div>
        </div> -->
      </div>
    </div>
  </div>
</template>

<script>
import { usePriceHistory } from '../composables/usePriceHistory'
import { getChannelIcon, getTimeUnit } from '../config/priceHistory'
import Chart from 'chart.js/auto'
import 'chartjs-adapter-date-fns'

export default {
  name: 'PriceHistoryChart',
  props: {
    productId: {
      type: String,
      required: true
    },
    autoLoad: {
      type: Boolean,
      default: true
    }
  },
  emits: ['data-loaded', 'error'],
  setup(props, { emit }) {
    const {
      priceHistory,
      loading,
      error,
      selectedPeriod,
      chartData,
      priceStats,
      availablePeriods,
      fetchPriceHistory,
      updatePeriod,
      formatPrice,
      formatDate,
      reset
    } = usePriceHistory()

    return {
      priceHistory,
      loading,
      error,
      selectedPeriod,
      chartData,
      priceStats,
      availablePeriods,
      fetchPriceHistory,
      updatePeriod,
      formatPrice,
      formatDate,
      reset,
      emit
    }
  },
  data() {
    return {
      chartInstance: null
    }
  },
  watch: {
    chartData: {
      handler() {
        console.log('PriceHistoryChart: chartData watcher triggered. chartData.datasets.length:', this.chartData.datasets.length);
        this.$nextTick(() => {
          this.initChart();
        });
      },
      deep: true
    },
    
  },
    mounted() {
    // With :key prop in parent, this component will remount when productId changes.
    // So, we can directly load data here.
    this.loadData();
  },
  beforeUnmount() {
    if (this.chartInstance) {
      this.chartInstance.destroy()
    }
  },
  methods: {
    // 通路圖示對應（從設定檔獲取）
    getChannelIconClass(channel) {
      return getChannelIcon(channel)
    },

    // 處理時間範圍變更
    async handlePeriodChange(days) {
      try {
        await this.updatePeriod(this.productId, days)
        this.emit('data-loaded', this.priceHistory)
      } catch (err) {
        this.emit('error', err)
      }
    },

    // 初始化圖表
    initChart() {
      console.log('PriceHistoryChart: initChart called. Current chartInstance:', this.chartInstance);
      if (!this.$refs.chartCanvas || !this.chartData.datasets.length) {
        console.log('PriceHistoryChart: initChart skipped due to missing canvas or empty datasets.');
        return;
      }
      
      const ctx = this.$refs.chartCanvas.getContext('2d')
      if (!ctx) {
        console.error('PriceHistoryChart: Failed to get 2D context from canvas.');
        return;
      }
      
      if (this.chartInstance) {
        this.chartInstance.destroy()
      }
      
      this.chartInstance = new Chart(ctx, {
        type: 'line',
        data: this.chartData,
        options: {
          responsive: true,
          maintainAspectRatio: false,
          interaction: {
            intersect: false,
            mode: 'index'
          },
          scales: {
            x: {
              type: 'time',
              time: {
                unit: getTimeUnit(this.selectedPeriod),
                tooltipFormat: 'MMM dd, yyyy HH:mm',
                displayFormats: {
                  day: 'MMM dd',
                  week: 'MMM dd'
                }
              },
              title: {
                display: true,
                text: '日期'
              },
              grid: {
                display: true,
                color: 'rgba(0,0,0,0.1)'
              }
            },
            y: {
              title: {
                display: true,
                text: '價格 (USD)'
              },
              ticks: {
                callback: function(value) {
                  return '$' + value.toFixed(2)
                }
              },
              grid: {
                display: true,
                color: 'rgba(0,0,0,0.1)'
              }
            }
          },
          plugins: {
            tooltip: {
              callbacks: {
                title: (context) => {
                  return this.formatDate(context[0].parsed.x)
                },
                label: (context) => {
                  return `${context.dataset.label}: ${this.formatPrice(context.parsed.y)}`
                }
              }
            },
            legend: {
              display: true,
              position: 'top'
            }
          },
          elements: {
            point: {
              radius: 3,
              hoverRadius: 6
            },
            line: {
              tension: 0.1
            }
          }
        }
      });
      console.log('PriceHistoryChart: New chart instance created.');
    },

    // 載入資料
    async loadData() {
      console.log('PriceHistoryChart: loadData called for productId:', this.productId);
      if (!this.productId) return
      
      // 重置之前的數據以避免顯示上一次的資料
      this.reset()
      console.log('PriceHistoryChart: After reset in loadData. priceHistory.value:', JSON.stringify(this.priceHistory.price_history));
      
      try {
        await this.fetchPriceHistory(this.productId)
        console.log('PriceHistoryChart: Data fetched. priceHistory.value:', JSON.stringify(this.priceHistory.price_history));
        this.emit('data-loaded', this.priceHistory)
      } catch (err) {
        this.emit('error', err)
      }
    },

    // 刷新資料
    refresh() {
      return this.loadData()
    }
  }
}
</script>

<style scoped>
.price-history-container {
  background: #fff;
  border-radius: 8px;
  padding: 1rem;
}

.chart-container {
  background: #fafafa;
  border-radius: 6px;
  padding: 1rem;
}

.btn-group-sm .btn {
  font-size: 0.8rem;
  padding: 0.25rem 0.5rem;
}

.opacity-50 {
  opacity: 0.5;
}

.badge {
  font-size: 0.75rem;
}

.bg-light {
  background-color: #f8f9fa !important;
}

.text-success {
  color: #198754 !important;
}

.text-danger {
  color: #dc3545 !important;
}

.text-primary {
  color: #0d6efd !important;
}

.text-info {
  color: #0dcaf0 !important;
}
</style>